/**
 * Helpers puros de jerarquía de roles. Espejan las reglas que el backend
 * enforza en `AuthzPolicyService` (ADR-015 §6).
 *
 *  - `actorPuedeOperarSobre`: equivalente a `assertHierarchyAbove` —
 *    el actor debe tener `nivel > target.nivel`.
 *  - `rolesQueActorPuedeAsignar`: filtra el catálogo de roles a los que
 *    el actor puede otorgar. Equivalente a `assertCanChangeRole`
 *    aplicado en la dirección "newRol".
 *  - `esUltimoSuperAdmin`: complemento de `assertNotLastSuperAdmin`. La UI
 *    no puede confirmarlo por sí sola; necesita el conteo de SuperAdmins
 *    activos (vía cache o `GET /admin/users?...` filtrado).
 *
 * Sin React, sin red. Apta para tests Vitest puros y para uso en
 * `canAccess` y selectores de admin (Fases 10-16).
 */

import { NIVEL_SUPER_ADMIN, type Rol, type SessionUser } from './usuario.entity';

/**
 * ¿El actor `A` puede mutar al target con rol `targetRol`? Verdadero si
 * `A.rol.nivel > targetRol.nivel`. Falso si son iguales o si el target
 * tiene mayor nivel.
 */
export function actorPuedeOperarSobre(
  actor: Pick<SessionUser, 'rol'> | null | undefined,
  targetRol: Pick<Rol, 'nivel'> | null | undefined,
): boolean {
  if (!actor || !targetRol) return false;
  // SuperAdmin bypass: acceso total a cualquier nivel (espeja
  // `assertHierarchyAbove` del backend). `esUltimoSuperAdmin` se valida
  // aparte y aplica también al propio SA.
  if (actor.rol.nivel === NIVEL_SUPER_ADMIN) return true;
  return actor.rol.nivel > targetRol.nivel;
}

/**
 * Del catálogo completo de roles del sistema, devuelve solo los que el
 * actor puede ASIGNAR (paso "newRol" de `assertCanChangeRole`).
 * Un actor solo puede asignar roles estrictamente inferiores al suyo.
 */
export function rolesQueActorPuedeAsignar<R extends Pick<Rol, 'nivel'>>(
  actor: Pick<SessionUser, 'rol'> | null | undefined,
  todosLosRoles: ReadonlyArray<R>,
): R[] {
  if (!actor) return [];
  return todosLosRoles.filter((r) => r.nivel < actor.rol.nivel);
}

/**
 * ¿Es seguro modificar a este target sin dejar al sistema sin
 * SuperAdmins activos?
 *
 * `superAdminsActivos` es el conteo total actual de usuarios SuperAdmin
 * con `estado === 'Activo'`. El backend lo enforza con
 * `assertNotLastSuperAdmin`; el frontend lo replica para deshabilitar
 * el botón antes de que el backend devuelva 409.
 */
export function esUltimoSuperAdmin(
  target: Pick<Rol, 'nivel'> | null | undefined,
  superAdminsActivos: number,
): boolean {
  if (!target) return false;
  return target.nivel === NIVEL_SUPER_ADMIN && superAdminsActivos <= 1;
}

/**
 * ¿El target es el mismo usuario que el actor? Equivalente del
 * `assertNotSelf` del backend (ADR-015 §6).
 */
export function esSelf(
  actorId: number | undefined | null,
  targetId: number | undefined | null,
): boolean {
  if (actorId === undefined || actorId === null) return false;
  if (targetId === undefined || targetId === null) return false;
  return actorId === targetId;
}
